import React, { useEffect, useState } from "react";
import { useQuery } from "react-query";
import styled from "styled-components";
import { fetchUserWithCompany } from "../../../../api/user";
import BasePage from "../../../BasePage";
import Container from "../../../library/Container";
import Loader from "../../../library/Loader";
import Button from "../../../library/Button";
import TextLink from "../../../library/TextLink";
import { useBoolean } from "../../../../hooks";
import CriticalEmployerSettingsForm from "../../../CriticalEmployerSettingsForm";
import { EmployerCriticalField } from "../../../../types";
import Employer from "../../../../apiResources/Employer";
import {
  DEFAULT_COMPANY_LOGO_URL,
  DEFAULT_USER_AVATAR_URL,
} from "../../../../constants";

const Success = () => {
  return (
    <BasePage title="Job Created" backUrl="/employer/jobs">
      <Container>
        <PageContent />
      </Container>
    </BasePage>
  );
};

const PageContent = () => {
  const { isLoading, data: user } = useQuery(
    "userWithCompany",
    fetchUserWithCompany,
    {
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false,
    }
  );
  const [isEditing, setIsEditing] = useBoolean();
  const [missingFields, setMissingFields] = useState<EmployerCriticalField[]>(
    []
  );

  useEffect(() => {
    setMissingFields(calculateMissingCriticalFields(user));
  }, [user]);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div>
      <Heading>Your job has been posted successfully 🎉</Heading>
      {missingFields.length === 0 && (
        <>
          <IntroPara>
            Your job is now live on Hiring Hub and ready for recruiters to start
            sending you applicants.
          </IntroPara>
          <div>
            <TextLink href="/employer/jobs">Return to Your Jobs</TextLink> or{" "}
            <TextLink href="/employer/jobs/new">Post another job</TextLink>
          </div>
        </>
      )}

      {missingFields.length > 0 && (
        <>
          <IntroPara>
            Before you go, we can see there&apos;s some key information missing
            from your company&apos;s profile page. Our data shows that companies
            with fully-populated, rich company profiles are more successful on
            the marketplace as it helps recruitment agencies sell your
            opportunity to candidates and drives higher engagement, while
            preventing them having to come direct to you for further
            information. We highly recommend completing your profile. You can
            quickly add the missing data below:
          </IntroPara>
          {!isEditing && (
            <ButtonGroup>
              <ButtonGroupItem>
                <Button primary onClick={setIsEditing.on}>
                  Yes, update profile now
                </Button>
              </ButtonGroupItem>
              <ButtonGroupItem>
                <TextLink href="/employer/jobs">
                  No thanks, I&apos;ll update it later
                </TextLink>
              </ButtonGroupItem>
            </ButtonGroup>
          )}
        </>
      )}

      {isEditing && (
        <>
          <Footer>
            <TextLink href="/employer/jobs">
              Cancel, I&apos;ll update it later
            </TextLink>
          </Footer>
          <CriticalEmployerSettingsForm
            missingFields={missingFields}
            user={user}
            company={user.company}
          />
        </>
      )}
    </div>
  );
};

const isMissingSocialLink = (company: Employer["attributes"]) => {
  return [
    company.twitterUsername,
    company.instagramUsername,
    company.facebookUrl,
    company.linkedinUrl,
    company.glassdoorUrl,
  ].every((val) => !val);
};

const isMissingBannerImage = (company: Employer["attributes"]) => {
  return [
    company.employerProfileImageUrl,
    company.employerProfileImage2Url,
    company.employerProfileImage3Url,
  ].every((val) => !val);
};

const calculateMissingCriticalFields = (user: any) => {
  const fields: EmployerCriticalField[] = [];

  if (!user) {
    return fields;
  }

  if (!user.isAdmin) {
    if (user.avatar === DEFAULT_USER_AVATAR_URL) {
      fields.push("avatar");
    }

    return fields;
  }

  const company = user.company;

  if (!company.profile) fields.push("profile");
  if (company.logoUrl === DEFAULT_COMPANY_LOGO_URL) fields.push("logo");
  if (user.avatar === DEFAULT_USER_AVATAR_URL) fields.push("avatar");
  if (!company.websiteAddress) fields.push("website");
  if (isMissingBannerImage(company)) fields.push("bannerImages");
  if (isMissingSocialLink(company)) fields.push("social");
  if (!company.townOrCity) fields.push("location");
  if (!company.numberOfEmployees) fields.push("numberOfEmployees");

  return fields;
};

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const ButtonGroupItem = styled.div`
  margin-bottom: 1.5rem;
  text-align: center;
`;

const Heading = styled.h2`
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 1.5rem;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const IntroPara = styled.p`
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 1.5rem;
  font-size: 18px !important;
`;

export default Success;
